import React, { useContext, useMemo } from 'react';

import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';

import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';
import { createHeadingLink } from '@confluence/editor-heading-links/entry-points/createHeadingLink.ts';
import {
	createNestedRendererData,
	type NestedAdfRendererWrapper,
	type NestedAdfRendererWrapperProps,
} from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

type NestedAdfRendererWrapperComponentProps = NestedAdfRendererWrapperProps & {
	Renderer: React.FC<any>;
	appearance?: string;
	rendererExtensionOptions: React.ContextType<typeof RendererExtensionContext>;
	getHeadingLinkPath?: () => string | null;
};

export type NestedAdfRendererWrapperProviderProps = {
	children: (RendererWrapper: NestedAdfRendererWrapper) => React.ReactNode;
	Renderer: React.FC<any>;
	appearance?: string;
};

const NestedAdfRendererWrapperComponent = ({
	Renderer,
	appearance,
	rendererExtensionOptions,
	adf,
	targetContentId,
	targetSpaceKey,
	mediaToken,
	headingIdPrefix,
	getHeadingLink,
	getHeadingLinkPath,
}: NestedAdfRendererWrapperComponentProps) => {
	const rendererAppearance = appearance === 'full-page' ? 'full-page' : 'full-width';
	const rendererData = useMemo(() => createNestedRendererData(adf), [adf]);
	const allowHeadingAnchorLinks = useMemo(() => {
		// Only override the heading link when we have a real path builder (editor /
		// live-page paths), where the platform default would otherwise produce the
		// *edit* URL. On the view-page renderer path `getHeadingLinkPath` is
		// undefined, so we omit the override entirely and let the platform default
		// (`getCurrentUrlWithHash` — current page URL + hash) apply, matching normal
		// (non-included) headings.
		const resolvedGetHeadingLink =
			getHeadingLink ??
			(getHeadingLinkPath
				? (headingId: string) => createHeadingLink(getHeadingLinkPath, headingId) ?? ''
				: undefined);

		return {
			allowNestedHeaderLinks: true,
			...(resolvedGetHeadingLink ? { getHeadingLink: resolvedGetHeadingLink } : {}),
		};
	}, [getHeadingLink, getHeadingLinkPath]);

	return (
		<RendererActionsContext>
			<RendererExtensionContext.Provider value={rendererExtensionOptions}>
				<Renderer
					contentId={targetContentId}
					spaceKey={targetSpaceKey}
					data={rendererData}
					appearance={rendererAppearance}
					hasInlineComments={false}
					mediaToken={mediaToken}
					headingIdPrefix={headingIdPrefix}
					allowHeadingAnchorLinks={allowHeadingAnchorLinks}
				/>
			</RendererExtensionContext.Provider>
		</RendererActionsContext>
	);
};

export const NestedAdfRendererWrapperProvider = ({
	children,
	Renderer,
	appearance,
}: NestedAdfRendererWrapperProviderProps): React.JSX.Element => {
	const { getExtensionHandlers, populateExtensionProviders, getHeadingLinkPath } =
		useContext(ClassicEditorContext);

	const contextRendererExtensionOptions = useMemo(
		() => ({
			getExtensionHandlers,
			populateExtensionProviders,
		}),
		[getExtensionHandlers, populateExtensionProviders],
	);

	const RendererWrapper = useMemo(
		() => (props: NestedAdfRendererWrapperProps) => (
			<NestedAdfRendererWrapperComponent
				{...props}
				Renderer={Renderer}
				appearance={appearance}
				getHeadingLinkPath={getHeadingLinkPath}
				rendererExtensionOptions={contextRendererExtensionOptions}
			/>
		),
		[appearance, Renderer, contextRendererExtensionOptions, getHeadingLinkPath],
	);

	return <>{children(RendererWrapper)}</>;
};
